import React, { useEffect, useMemo, useState } from 'react';

import { API } from 'constants';
import client from '../../../services/library-api';
import assistantClient from '../../../services/assistant-api';

import { useSearchParams } from 'react-router-dom';
import { useManageSearchParams } from '../../../hooks/useManageSearchParams';

import { TAB_TYPE } from '../../../constants/playbooks';
import { defaultErrorMessage } from '../../../constants/errorMessages';

import Loading from '../../../components/Loading';
import TabNavigation from '../TabNavigation/TabNavigation';
import TabsViewHeader from '../TabsViewHeader/TabsViewHeader';
import FormTabSection from '../FormTabSection/FormTabSection';
import PromptTabSection from '../PromptTabSection/PromptTabSection';
import OverviewTabSection from '../OverviewTabSection/OverviewTabSection';
import TabViewsTopControls from '../TabViewsTopControls/TabViewsTopControls';

export const containerClassName = 'w-full min-[800px]:max-w-[800px] 3xl:max-w-[880px] mx-auto';
export const pageHorizontalPaddings = 'px-5 sm:px-6 md:px-8 min-[1124px]:px-[40px] xl:px-[60px]';

const PlaybookTabsView = ({
    playbookDetail,
    setPlaybookDetail,
    playbookContentData,
    backLinkHref,
}) => {
    const { name, description, icon_color } = playbookDetail;

    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');

    const tabs = playbookDetail.default_view.view_content.tabs;
    const tabsToDisplay = useMemo(() => {
        if (!tabs || !tabs.length) return [];
        return tabs.filter((tab) => tab.is_live).sort((a, b) => a.order - b.order);
    }, [tabs]);
    const [activeTab, setActiveTab] = useState(tabsToDisplay[0]);

    const activeTabType = activeTab.type;
    const activeTabScenarioId = activeTab.detail?.scenario;
    const activeTabPromptId = activeTab.detail?.chat_prompt;

    const [scenariosData, setScenariosData] = useState(null);
    const [promptsData, setPromptsData] = useState(null);

    const { deleteSearchParam } = useManageSearchParams();

    useEffect(() => {
        const fetchTabData = async ({ client, route, activeId, setData, idsToFetch }) => {
            try {
                // if active tab needs load data - fetch active Tab data first
                if (activeId) {
                    try {
                        const { data } = await client.get(`${route}${activeId}/?shared=true`);
                        setData({ [data.id]: { data } });
                    } catch (e) {
                        setData({
                            [activeId]: { error: { message: defaultErrorMessage } },
                        });
                    }
                }

                if (!idsToFetch.length) return;

                // fetch all others tabs data
                const data = await Promise.allSettled(
                    idsToFetch.map((id) => client.get(`${route}${id}/?shared=true`))
                );

                const result = data.reduce((acc, { status, value }, index) => {
                    if (status === 'fulfilled') {
                        const data = value.data;
                        return { ...acc, [data.id]: { data } };
                    }
                    if (status === 'rejected') {
                        const id = idsToFetch[index];
                        return { ...acc, [id]: { error: { message: defaultErrorMessage } } };
                    }
                    return acc;
                }, {});

                setData((prev) => ({ ...(prev || {}), ...result }));
            } catch (e) {}
        };

        const scenarioIds = tabsToDisplay.reduce((ids, tab) => {
            if (tab.type === TAB_TYPE.form) {
                const scenario = tab.detail.scenario;
                if (scenario && !ids.includes(scenario) && scenario !== activeTabScenarioId) {
                    ids.push(scenario);
                }
            }
            return ids;
        }, []);

        const promptIds = tabsToDisplay.reduce((ids, tab) => {
            if (tab.type === TAB_TYPE.prompt) {
                const prompt = tab.detail.chat_prompt;
                if (prompt && !ids.includes(prompt) && prompt !== activeTabPromptId) {
                    ids.push(prompt);
                }
            }
            return ids;
        }, []);

        void fetchTabData({
            client,
            route: API.ROUTES.library.scenario,
            activeId: activeTabScenarioId,
            idsToFetch: scenarioIds,
            setData: setScenariosData,
        });

        void fetchTabData({
            client: assistantClient,
            route: API.ROUTES.assistant.chatPrompt,
            activeId: activeTabPromptId,
            idsToFetch: promptIds,
            setData: setPromptsData,
        });
    }, []);

    useEffect(() => {
        if (mode) {
            deleteSearchParam('mode');
        }
    }, [mode]);

    const isLoading =
        (!scenariosData && activeTabType === TAB_TYPE.form) ||
        (!promptsData && activeTabType === TAB_TYPE.prompt);

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <Loading />
            </div>
        );
    }

    return (
        <div className="page-position pt-5 lg:pt-8 xl:pt-[40px] bg-white flex flex-col gap-5 hide-scrollbar overflow-y-auto lg:overflow-hidden">
            <div className="flex flex-col gap-5 lg:gap-6 xl:gap-[40px]">
                <TabViewsTopControls
                    backLinkHref={backLinkHref}
                    playbookDetail={playbookDetail}
                    setPlaybookDetail={setPlaybookDetail}
                    playbookContentFormData={playbookContentData}
                />

                <TabNavigation
                    tabs={tabsToDisplay}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            </div>

            <div className="flex flex-col gap-5 lg:flex-grow lg:overflow-y-auto pb-[68px] min-[1380px]:pb-[40px]">
                <TabsViewHeader
                    playbook={{ name, description }}
                    icon_color={icon_color}
                    type={activeTabType}
                    scenario={scenariosData?.[activeTabScenarioId]}
                    prompt={promptsData?.[activeTabPromptId]}
                />

                <div className={pageHorizontalPaddings}>
                    <div className={containerClassName}>
                        {tabs.map((tab) => {
                            const { type, order, detail } = tab;
                            const isActive = activeTab.order === order;
                            const scenarioId = detail?.scenario;
                            const promptId = detail?.chat_prompt;

                            if (type === TAB_TYPE.prompt) {
                                const className = isActive
                                    ? 'h-full visible'
                                    : 'h-0 max-h-0 overflow-hidden invisible pointer-events-none';

                                return (
                                    <div key={order} className={className}>
                                        <PromptTabSection
                                            promptData={promptsData?.[promptId]}
                                            setPromptsData={setPromptsData}
                                        />
                                    </div>
                                );
                            }

                            return (
                                <div key={order} className={isActive ? 'block' : 'hidden'}>
                                    {type === TAB_TYPE.overview && <OverviewTabSection tab={tab} />}

                                    {type === TAB_TYPE.form && (
                                        <FormTabSection scenario={scenariosData?.[scenarioId]} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlaybookTabsView;
